// 용량 표시 유틸리티 — 관리자 설정(device_catalog.storageOptions)이 단일 진실 소스(SSOT)
// 관리자 페이지에 설정되지 않은 용량은 사용자에게 노출하지 않는다.
// (미설정 용량은 '기타 용량 직접 입력' 경로로만 진입 가능하며, 이 경우 가격 미설정으로 처리됨)
import { STANDARD_STORAGE_OPTIONS, STORAGE_DISPLAY_PRIORITY } from '../constants/standardStorageOptions';

/**
 * 관리자 설정 용량 목록을 표시용으로 정리 (중복 제거 + 용량 크기순 정렬)
 * 관리자 페이지 가격 관리 그리드와 동일한 목록을 반환한다.
 */
export const getSmartStorageOptions = (
  deviceType: 'smartphone' | 'tablet',
  brand: 'Apple' | 'Samsung' | 'LG',
  adminConfiguredOptions: string[] = []
): string[] => {
  return getStorageOptionsWithStatus(deviceType, brand, adminConfiguredOptions)
    .map(option => option.storage);
};

/**
 * 🔢 스토리지 용량에서 숫자 추출 (정렬용)
 */
export const extractStorageSize = (storage: string): number => {
  // "256GB" → 256, "1TB" → 1024, "2TB" → 2048
  const match = storage.match(/(\d+(?:\.\d+)?)\s*(GB|TB|MB)/i);
  if (!match) return 0;
  
  const number = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  
  switch (unit) {
    case 'TB': return number * 1024;
    case 'GB': return number;
    case 'MB': return number / 1024;
    default: return number;
  }
};

/**
 * 🎯 용량별 상태 정보 생성
 */
export interface StorageOptionInfo {
  storage: string;
  hasAdminPrice: boolean; // 관리자가 가격 설정했는지
  isStandard: boolean;    // 표준 용량인지 
  priority: number;       // 표시 우선순위
  description: string;    // 용량 설명
}

export const getStorageOptionsWithStatus = (
  deviceType: 'smartphone' | 'tablet',
  brand: 'Apple' | 'Samsung' | 'LG',
  adminConfiguredOptions: string[] = []
): StorageOptionInfo[] => {
  // 업계 표준 용량 목록은 isStandard 메타데이터 계산에만 사용 (목록에 강제 병합하지 않음)
  const standardOptions: readonly string[] = deviceType === 'smartphone'
    ? (brand === 'Apple' ? STANDARD_STORAGE_OPTIONS.smartphone.iPhone
                          : STANDARD_STORAGE_OPTIONS.smartphone.Samsung) // LG는 Samsung과 동일한 표준 용량 사용
    : (brand === 'Apple' ? STANDARD_STORAGE_OPTIONS.tablet.iPad
                          : STANDARD_STORAGE_OPTIONS.tablet.Galaxy); // LG는 Galaxy와 동일한 표준 용량 사용

  // 관리자 설정 용량만 사용: 공백 제거 + 중복 제거
  const uniqueAdminOptions = Array.from(new Set(
    adminConfiguredOptions
      .map(option => (option || '').trim())
      .filter(option => option !== '')
  ));

  // 용량 크기순 정렬 (우선순위 정의 우선, 이후 숫자 크기순)
  const sortedOptions = uniqueAdminOptions.sort((a, b) => {
    const aPriority = STORAGE_DISPLAY_PRIORITY[a];
    const bPriority = STORAGE_DISPLAY_PRIORITY[b];

    if (aPriority && bPriority) {
      return aPriority - bPriority;
    }

    return extractStorageSize(a) - extractStorageSize(b);
  });

  return sortedOptions.map(storage => ({
    storage,
    hasAdminPrice: true, // 목록 전체가 관리자 설정 용량
    isStandard: standardOptions.includes(storage),
    priority: STORAGE_DISPLAY_PRIORITY[storage] || 999,
    description: getStorageDescription(storage)
  }));
};

/**
 * 📝 용량별 설명 생성
 */
export const getStorageDescription = (storage: string): string => {
  const size = extractStorageSize(storage);
  
  if (size <= 64) return '기본 용량';
  if (size <= 128) return '표준 용량';
  if (size <= 256) return '권장 용량';
  if (size <= 512) return '대용량';
  if (size <= 1024) return '최고 용량';
  return '프로 용량';
};

/**
 * 🎨 용량별 UI 테마 색상
 */
export const getStorageThemeColor = (hasAdminPrice: boolean, isStandard: boolean) => {
  if (hasAdminPrice) {
    return {
      borderColor: '#6366f1', // 가격 설정됨: 파란색
      backgroundColor: 'rgba(99, 102, 241, 0.05)',
      chipColor: 'primary' as const,
      chipLabel: '가격미설정'
    };
  }
  
  if (isStandard) {
    return {
      borderColor: '#10b981', // 표준 용량: 초록색
      backgroundColor: 'rgba(16, 185, 129, 0.05)', 
      chipColor: 'success' as const,
      chipLabel: '표준용량'
    };
  }
  
  return {
    borderColor: '#f59e0b', // 특별 용량: 주황색
    backgroundColor: 'rgba(245, 158, 11, 0.05)',
    chipColor: 'warning' as const,
    chipLabel: '특별용량'
  };
};







